import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';
import classNames from 'classnames';
import Message from 'components/Message';
import { TooltipToggle, TooltipContent } from 'components/Tooltip';
import { bem } from 'helpers/class-name';
import './PillGraph.scss';

const messages = defineMessages({
  noData: {
    id: 'components.pill-graph',
    defaultMessage: 'No open spots or reservations',
  },
});

const { block, element, modifier } = bem('pill-graph');

export const PILL_GRAPH_COLORS = {
  GREEN: 'green',
  LIGHT_GREEN: 'light-green',
  GRAY: 'gray',
  BLUE: 'blue',
  DARK_BLUE: 'dark-blue',
  RED: 'red',
  PURPLE: 'purple',
};

export const PILL_GRAPH_DATA_TYPES = {
  MARKER: 'marker',
  ALLOCATION: 'allocation',
};

export const PillGraphLegend = ({ className, items }) => (
  <ul className={classNames(element('legend'), className)}>
    {items.map(({ key, value, color, type }) => (
      <li
        key={key}
        className={classNames(
          element('legend-item'),
          modifier(`legend-item-${color}`),
          modifier(`legend-item-${type}`)
        )}>
        {value}
      </li>
    ))}
  </ul>
);

PillGraphLegend.propTypes = {
  className: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      color: PropTypes.oneOf(Object.values(PILL_GRAPH_COLORS)),
      value: PropTypes.node,
      type: PropTypes.oneOf(Object.values(PILL_GRAPH_DATA_TYPES)),
    })
  ),
};

const PillGraph = ({ data, large, className, name }) => {
  const filteredData = data.filter(({ value }) => value > 0);
  const totalValues = filteredData.reduce((sum, { value }) => sum + value, 0);
  const ticks = new Array(Math.ceil(totalValues / 10)).fill(null).map((nil, index) => 10 * index);

  if (ticks[ticks.length - 1] >= totalValues - 1) {
    ticks[ticks.length - 1] = totalValues;
  } else {
    ticks.push(totalValues);
  }

  if (totalValues === 0) {
    return <Message message={messages.noData} />;
  }

  return (
    <div className={classNames(block(), { [modifier('large')]: large }, className)}>
      <div className={element('bar')}>
        {filteredData.map(({ key, value, color, label }) => {
          const tooltipKey = `pill-graph-${name}-${key}-${value}`;

          return (
            <TooltipToggle
              key={tooltipKey}
              name={tooltipKey}
              className={element('bar-container')}
              style={{ width: `${(value / totalValues) * 100}%` }}>
              {Array(value)
                .fill(null)
                .map((nil, i) => (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${key}${i}`}
                    className={classNames(element('block'), modifier(color))}
                    data-testid={`pill-graph-${color}`}
                  />
                ))}
              <TooltipContent key={tooltipKey} name={tooltipKey} className={element('tooltip')} hoverable>
                {label}
              </TooltipContent>
            </TooltipToggle>
          );
        })}
      </div>
      <div className={element('x-axis')}>
        {ticks.map(tick => {
          const tickStyle = { left: `${(tick / totalValues) * 100}%` };
          return (
            <div key={tick} className={element('tick')} style={tickStyle}>
              {tick}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const dataShape = PropTypes.shape({
  key: PropTypes.string,
  label: PropTypes.node,
  value: PropTypes.number,
  color: PropTypes.oneOf(Object.values(PILL_GRAPH_COLORS)),
});

PillGraph.propTypes = {
  data: PropTypes.arrayOf(dataShape),
  large: PropTypes.bool,
  className: PropTypes.string,
  name: PropTypes.string,
};

export default PillGraph;
